import bassStyleData from '@/data/bassStyles.json';
import { parseChord } from './chords';
import { approachTone, resolveStarterScale } from './scaleContract';

const mod = (value, base = 12) => ((value % base) + base) % base;
const chordForBar = (chords, bar) => chords.find((item) => item.bar === bar) || chords[bar % Math.max(1, chords.length)] || chords[0];
const pitchInRange = (pitchClass, low, high, target = low) => Array.from({ length: high - low + 1 }, (_, index) => low + index)
  .filter((pitch) => mod(pitch) === mod(pitchClass)).sort((a, b) => Math.abs(a - target) - Math.abs(b - target) || a - b)[0];

export function bassRolePitch(role, symbol, nextSymbol, currentPitch, scale, voice = '808') {
  const low = voice === '808' ? 36 : 40; const high = voice === '808' ? 47 : 52;
  const chord = parseChord(symbol); const next = parseChord(nextSymbol || symbol);
  if (!chord) return low;
  const root = pitchInRange(chord.rootPitch, low, high, currentPitch ?? low + 4);
  if (role === 'approach') return approachTone(next?.rootPitch ?? chord.rootPitch, currentPitch ?? root, scale, low, high);
  if (role === 'fifth') return pitchInRange(chord.rootPitch + 7, low, high, root + 5);
  if (role === 'octave') return pitchInRange(chord.rootPitch, low, high, high);
  return root;
}

export function kickStartsForClip(chordClip, drumClip) {
  if (!chordClip || !drumClip?.pattern) return [];
  const pattern = drumClip.pattern; const grid = Number(pattern.stepsPerBar || 16);
  const kick = pattern.steps?.kick || [];
  const chordStartBeat = Number(chordClip.start_bar) * 4;
  const chordEndBeat = chordStartBeat + Number(chordClip.bars || chordClip.pattern?.patternBars || 1) * 4;
  const drumStartBeat = Number(drumClip.start_bar) * 4 - Number(drumClip.offset || 0) * 4;
  return kick.map((velocity, step) => velocity > 0 ? { absolute: drumStartBeat + step * 4 / grid, velocity } : null).filter((hit) => hit && hit.absolute >= chordStartBeat && hit.absolute < chordEndBeat)
    .map((hit) => ({ start: hit.absolute - chordStartBeat, velocity: hit.velocity }));
}

/**
 * Kick starts (beats, relative to the chord clip) from EVERY drum pattern clip under the chord clip.
 * Each drum clip loops its pattern inside its visible window, so a 1-bar drum clip duplicated across
 * bars 1-4, or one 4-bar drum clip, both guide all four bars. Duplicate hits are merged.
 */
export function kickStartsForArrangement(tracks, chordClip) {
  if (!chordClip) return [];
  const chordStart = Number(chordClip.start_bar) * 4;
  const chordEnd = chordStart + Number(chordClip.bars || chordClip.pattern?.patternBars || 1) * 4;
  const hits = new Map();
  (tracks || []).flatMap((track) => track.clips || []).forEach((clip) => {
    if (!clip.isPattern || clip.patternType !== 'drums' || !clip.pattern) return;
    const grid = Number(clip.pattern.stepsPerBar || 16);
    const kick = clip.pattern.steps?.kick || [];
    const sourceBeats = Math.max(1, Number(clip.pattern.patternBars || 1)) * 4;
    const windowStart = Number(clip.start_bar) * 4;
    const windowEnd = windowStart + Number(clip.bars || 0) * 4;
    const offsetBeats = Number(clip.offset || 0) * 4;
    const from = Math.max(windowStart, chordStart); const to = Math.min(windowEnd, chordEnd);
    if (from >= to) return;
    kick.forEach((velocity, step) => {
      if (!(velocity > 0)) return;
      const sourceBeat = step * 4 / grid;
      // First absolute time this source step plays inside the window, then every loop of the source.
      let absolute = windowStart + (((sourceBeat - offsetBeats) % sourceBeats) + sourceBeats) % sourceBeats;
      for (; absolute < to; absolute += sourceBeats) {
        if (absolute < from) continue;
        const key = Math.round((absolute - chordStart) * 64);
        hits.set(key, { start: key / 64, velocity: Math.max(velocity, hits.get(key)?.velocity || 0) });
      }
    });
  });
  return [...hits.values()].sort((a, b) => a.start - b.start);
}

export function findOverlappingKickClip(tracks, chordClip) {
  const start = Number(chordClip.start_bar); const end = start + Number(chordClip.bars || chordClip.pattern?.patternBars || 1);
  return (tracks || []).flatMap((track) => track.clips || []).find((clip) => clip.isPattern && clip.patternType === 'drums'
    && clip.start_bar < end && start < clip.start_bar + Number(clip.bars || 0) && kickStartsForClip(chordClip, clip).length) || null;
}

export function generateBassline({ chords, family = 'trap', packId = null, keySignature = 'C min', patternBars = 4, kickStarts = null } = {}) {
  const style = bassStyleData.families[family] || bassStyleData.families.trap;
  const grid = Number(style.stepsPerBar || 16); const scale = resolveStarterScale({ packId, family, keySignature });
  const events = [];
  const familyBar = (bar) => style.notes.forEach((item) => events.push({ bar, role: item.role, start: bar * 4 + (item.step - 1) * 4 / grid,
    length: Math.min(item.len * 4 / grid, (bar + 1) * 4 - (bar * 4 + (item.step - 1) * 4 / grid)), velocity: .9 }));
  if (kickStarts?.length) {
    for (let bar = 0; bar < patternBars; bar += 1) {
      const starts = kickStarts.filter((hit) => Math.floor(hit.start / 4) === bar).sort((a, b) => a.start - b.start);
      if (!starts.length) { familyBar(bar); continue; } // no kick in this bar: use the genre bass pattern
      starts.forEach((hit, index) => events.push({ start: hit.start, length: Math.max(1 / 16, Math.min((starts[index + 1]?.start ?? (bar + 1) * 4) - hit.start, (bar + 1) * 4 - hit.start)), bar,
        role: index === starts.length - 1 && bar < patternBars - 1 && chordForBar(chords, bar)?.symbol !== chordForBar(chords, bar + 1)?.symbol ? 'approach' : 'root', velocity: hit.velocity || .9 }));
    }
  } else {
    for (let bar = 0; bar < patternBars; bar += 1) familyBar(bar);
  }
  let previous = style.voice === '808' ? 40 : 44;
  const notes = events.map((event, index) => {
    const symbol = chordForBar(chords, event.bar)?.symbol;
    const nextSymbol = chordForBar(chords, Math.min(event.bar + 1, patternBars - 1))?.symbol;
    const pitch = bassRolePitch(event.role, symbol, nextSymbol, previous, scale, style.voice); previous = pitch;
    return { id: `bass-${index}`, start: event.start, length: event.length, pitch, velocity: event.velocity };
  });
  return { notes, roles: events.map((event) => event.role), voice: style.voice, mono: style.voice === '808', glide: style.voice === '808' && style.glide ? 80 : 0, scale: scale.name };
}

export const __bassDataVersion = bassStyleData.version;
